import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, 
  Loader2, 
  X, 
  FileText, 
  Image as ImageIcon, 
  Heart,
  Edit,
  ThumbsDown,
  RefreshCw,
  Check,
  CheckCircle,
  ExternalLink,
  AlertTriangle,
  Globe,
  Layers,
  Type,
  Camera,
  ChevronDown,
  Upload,
  Paperclip,
  CalendarDays
} from 'lucide-react';
import { 
  FaLinkedin, FaInstagram, 
  FaTwitter, FaPinterest, FaBlog, FaEnvelope, FaNewspaper, FaFacebook, FaMedium, FaYoutube, FaBox
} from 'react-icons/fa';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useUsageStatus } from '../../components/ui/UsageStatus';
import { Link } from 'react-router-dom';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// Platform configurations matching backend PLATFORM_SETTINGS
const platforms = [
  { key: 'x', label: 'X', icon: FaTwitter, color: '#000000', maxLength: 280, hasHeadline: false, hashtags: 3, image: '1200×675 (16:9)' },
  { key: 'linkedin', label: 'LinkedIn Beitrag', icon: FaLinkedin, color: '#0077B5', maxLength: 3000, hasHeadline: false, hashtags: 5, image: '1200×627 (1.91:1)' },
  { key: 'instagram', label: 'Instagram', icon: FaInstagram, color: '#E4405F', maxLength: 300, hasHeadline: false, hashtags: 5, image: '1080×1350 (4:5)' },
  { key: 'facebook', label: 'Facebook', icon: FaFacebook, color: '#1877F2', maxLength: 5000, hasHeadline: false, hashtags: 3, image: '1200×630 (1.91:1)' },
  { key: 'pinterest', label: 'Pinterest', icon: FaPinterest, color: '#BD081C', maxLength: 320, hasHeadline: true, headlineMax: 28, hashtags: 5, image: '1000×1500 (2:3)' },
  { key: 'medium', label: 'Medium', icon: FaMedium, color: '#000000', maxLength: 4000, hasHeadline: true, headlineMax: 67, hashtags: 0, image: '1200×628 (1.91:1)' },
  { key: 'blog', label: 'Blogartikel', icon: FaBlog, color: '#00D9C8', maxLength: 2800, hasHeadline: true, headlineMax: 60, hashtags: 0, image: '1200×628 (1.91:1)' },
  { key: 'newsletter', label: 'Newsletter', icon: FaEnvelope, color: '#F59E0B', maxLength: 600, hasHeadline: true, headlineMax: 70, hashtags: 0, image: null },
  { key: 'pressemitteilung', label: 'Pressemitteilung', icon: FaNewspaper, color: '#6B7280', maxLength: 5000, hasHeadline: true, headlineMax: 57, hashtags: 0, image: '1200×628 (1.91:1)' },
  { key: 'youtube', label: 'YouTube', icon: FaYoutube, color: '#FF0000', maxLength: 450, hasHeadline: true, headlineMax: 100, hashtags: 0, image: '1920×1080 (16:9)', isVideo: true },
  { key: 'produktbeschreibung', label: 'Produktbeschreibung', icon: FaBox, color: '#1e9ead', maxLength: 6000, hasHeadline: true, headlineMax: 80, hashtags: 0, image: '1200×900 (4:3)' },
];

const LANGUAGE_OPTIONS = [
  { code: 'de', label: 'Deutsch', abbr: 'DE' },
  { code: 'en', label: 'English', abbr: 'EN' },
  { code: 'pl', label: 'Polski', abbr: 'PL' },
  { code: 'fr', label: 'Français', abbr: 'FR' },
  { code: 'es', label: 'Español', abbr: 'ES' },
  { code: 'it', label: 'Italiano', abbr: 'IT' },
  { code: 'nl', label: 'Nederlands', abbr: 'NL' },
  { code: 'pt', label: 'Português', abbr: 'PT' },
  { code: 'cs', label: 'Čeština', abbr: 'CS' },
  { code: 'hu', label: 'Magyar', abbr: 'HU' },
  { code: 'sv', label: 'Svenska', abbr: 'SV' },
  { code: 'tr', label: 'Türkçe', abbr: 'TR' },
  { code: 'ro', label: 'Română', abbr: 'RO' },
  { code: 'lv', label: 'Latviešu', abbr: 'LV' },
];

const ELEMENT_OPTIONS = [
  { code: 'hauptprodukt', label: 'Hauptprodukt' },
  { code: 'markengesicht', label: 'Markengesicht' },
];

const Kreation = () => {
  const { getAuthHeader, activeProjectId, isProjectReady } = useAuth();
  const { t, lang } = useLanguage();
  const { usage, limitReached, warning, refetch: refetchUsage } = useUsageStatus();
  const [description, setDescription] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [files, setFiles] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState([]);
  const [contentCount, setContentCount] = useState(0);
  const [ideas, setIdeas] = useState([]);
  const [ideaCursor, setIdeaCursor] = useState(0); // welche Idee als nächstes ins Formular
  const [placeholderIdx, setPlaceholderIdx] = useState(0); // rotierender Ghost-Placeholder
  const [isLoadingIdeas, setIsLoadingIdeas] = useState(false);
  const [editingIdeaId, setEditingIdeaId] = useState(null);
  const [editedIdeaText, setEditedIdeaText] = useState('');
  const [userChannels, setUserChannels] = useState([]);
  const [activeBatchId, setActiveBatchId] = useState(null);
  const [videoLimit, setVideoLimit] = useState({ used: 0, limit: 0, remaining: 0 });
  const [selectedLanguage, setSelectedLanguage] = useState('de');
  const [outputLanguages, setOutputLanguages] = useState(['de']); // first = primary; multi = one post, multiple languages
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  // Primary language is always the first selected; keeps legacy `language` path intact
  useEffect(() => {
    setSelectedLanguage(outputLanguages[0] || 'de');
  }, [outputLanguages]);

  // Rotierender Placeholder aus vorhandenen Content-Ideen (instant, keine
  // Extra-Calls — nutzt die beim Mount geladene `ideas`-Liste). Wechselt alle
  // 4s durch die Ideen-Titel; fällt auf statischen Text zurück wenn leer.
  const ideaTitles = ideas.map(i => i?.title).filter(Boolean);
  useEffect(() => {
    if (ideaTitles.length < 2) return;
    const id = setInterval(() => {
      setPlaceholderIdx(i => (i + 1) % ideaTitles.length);
    }, 4000);
    return () => clearInterval(id);
  }, [ideaTitles.length]);
  const rotatingPlaceholder = ideaTitles.length > 0
    ? `${t.kreation.ideaPlaceholderPrefix}${ideaTitles[placeholderIdx % ideaTitles.length]}`
    : t.kreation.describeTopicFallback;
  // Platforms where multi-language (one post, several languages) fits the char limit
  const MULTILANG_PLATFORMS = ['instagram', 'facebook', 'linkedin'];
  const multiLangAllowed = selectedPlatforms.length > 0 && selectedPlatforms.every(p => MULTILANG_PLATFORMS.includes(p));
  const toggleLanguage = (code) => {
    setOutputLanguages(prev => {
      if (prev.includes(code)) {
        // don't allow removing the last remaining language
        if (prev.length === 1) return prev;
        return prev.filter(c => c !== code);
      }
      // if multi-language isn't allowed on current platform selection, replace instead of add
      if (!multiLangAllowed) return [code];
      return [...prev, code];
    });
  };
  const [selectedElement, setSelectedElement] = useState(null);
  const [showElementDropdown, setShowElementDropdown] = useState(false);
  const [keepInputText, setKeepInputText] = useState(false);
  const [keepUploadedPhotos, setKeepUploadedPhotos] = useState(false);
  const pollingIntervalRef = useRef(null);
  // Guard: ensures we fire the success/failed toast only ONCE per batch even
  // when multiple in-flight poll calls resolve after the batch completes.
  const batchCompleteHandledRef = useRef(false);
  const [enabledPlatforms, setEnabledPlatforms] = useState(null); // null = not loaded yet
  const [customChannels, setCustomChannels] = useState([]);
  const [brandRefImages, setBrandRefImages] = useState([]);
  const [selectedReferenceImageIds, setSelectedReferenceImageIds] = useState([]);
  const [customReferenceImage, setCustomReferenceImage] = useState(null); // { preview, base64 } — eigenes Referenzbild pro Post

  // Nur einmalig laden: Sprache + Video-Kontingent + Brand-Referenzen.
  // Projekt-abhängige Daten (Ideas, Channels, ContentCount, PlatformConfig)
  // werden ausschließlich im projekt-gescopten Effect unten geladen, sobald
  // isProjectReady === true. So werden Race Conditions beim Initial-Login
  // sauber vermieden.
  useEffect(() => {
    fetchDefaultLanguage();
    // Load brand reference images for the per-generation selector
    (async () => {
      try {
        const r = await axios.get(`${API}/brand/media`, { headers: getAuthHeader() });
        const list = (r.data?.media || r.data || []).filter(m => m.media_type === 'image').slice(0, 10);
        setBrandRefImages(list);
      } catch { /* no images — selector simply hidden */ }
    })();
    // Load video allowance from the user's current plan
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const r = await axios.get(`${API}/plans/my-plan`, { headers: { Authorization: `Bearer ${token}` } });
        const plan = r.data?.plan || {};
        const usage = r.data?.usage || {};
        const monthly = Number(plan?.limits?.videos_monthly || 0);
        const addon = Number(plan?.limits?.videos_addon_monthly || 0);
        const usedMonthly = Number(usage.videos_used || 0);
        const usedAddon = Number(usage.videos_addon_used || 0);
        const limit = monthly + addon;
        const used = usedMonthly + usedAddon;
        setVideoLimit({ used, limit, remaining: Math.max(0, limit - used) });
      } catch { /* keep defaults */ }
    })();
    fetchVideoLimit();

    // Cleanup polling on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Projekt-gescopte Daten: laufen erst, wenn AuthContext fertig initialisiert
  // ist UND ein activeProjectId feststeht. Bei Projektwechsel bricht der
  // AbortController alle noch offenen Requests des alten Projekts ab, damit
  // keine stale Antworten den neuen Zustand überschreiben können.
  useEffect(() => {
    if (!isProjectReady || !activeProjectId) return;
    const controller = new AbortController();
    const signal = controller.signal;

    fetchContentCount(signal);
    fetchIdeas(signal);
    fetchUserChannels(signal);
    fetchVideoLimit(signal);
    fetchPlatformConfig(signal);

    // Reset any transient UI state that was tied to the previous project
    setGeneratedContent([]);
    setEditingIdeaId(null);
    setSelectedReferenceImageIds([]);

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectId, isProjectReady]);

  const fetchDefaultLanguage = async () => {
    try {
      const response = await axios.get(`${API}/brand/preferences`, {
        headers: getAuthHeader()
      });
      const lang = response.data.content_language || 'de';
      setOutputLanguages([lang]); // selectedLanguage syncs via effect
    } catch (err) {
      // Default to 'de'
    }
  };

  const fetchPlatformConfig = async (signal) => {
    try {
      const response = await axios.get(`${API}/platform-config`, { headers: getAuthHeader(), signal });
      setEnabledPlatforms(response.data.enabled_platforms || []);
      setCustomChannels(response.data.custom_channels || []);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      // If endpoint fails, show all platforms (backwards compatible)
    }
  };

  // Filter platforms based on user's enabled platforms config
  const activePlatforms = (() => {
    if (enabledPlatforms === null || enabledPlatforms.length === 0) return platforms; // Show all if not configured
    const filtered = platforms.filter(p => enabledPlatforms.includes(p.key));
    // Add custom channels as platform options
    customChannels.filter(c => enabledPlatforms.includes(c.id)).forEach(c => {
      const mainField = c.fields?.[0];
      filtered.push({
        key: c.id, label: c.name, icon: Layers, color: '#A0AEC0',
        maxLength: mainField?.maxLength || 500, hasHeadline: false, hashtags: 0, image: null
      });
    });
    return filtered;
  })();

  const fetchContentCount = async (signal) => {
    try {
      const response = await axios.get(`${API}/content/count`, {
        headers: getAuthHeader(),
        signal,
      });
      setContentCount(response.data.count);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch count:', err);
    }
  };

  const fetchIdeas = async (signal) => {
    setIsLoadingIdeas(true);
    try {
      const response = await axios.get(`${API}/content/ideas`, {
        headers: getAuthHeader(),
        signal,
      });
      setIdeas(response.data.ideas || []);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch ideas:', err);
    } finally {
      setIsLoadingIdeas(false);
    }
  };

  // Füllt eine einzelne Idee ins Formular (Thema + Plattform + Sprache).
  const applyIdeaToForm = (idea) => {
    if (!idea) return;
    // Bei Personen mit mehreren Rollen: Rolle mitgeben, damit der Beitrag nur aus dieser Rolle (oder kombiniert) geschrieben wird
    setDescription(`${idea.title}\n\n${idea.description}${idea.role_label ? `\n\n(Rolle: ${idea.role_label}${idea.role_id === 'combined' ? ', mehrere Rollen verbinden' : ', nur aus dieser Rolle schreiben'})` : ''}`);
    if (idea.platform) setSelectedPlatforms([idea.platform]);
    if (idea.language) setOutputLanguages([idea.language]);
  };

  // "Generate Idea"-Button: nutzt die bereits geladene Ideen-Liste und geht
  // pro Klick zur nächsten. Ist die Liste leer/durch, wird EINMAL nachgeladen
  // (refresh, bis 90s) und danach wieder instant durchgeklickt.
  const handleGenerateIdea = async () => {
    if (ideaCursor < ideas.length) {
      applyIdeaToForm(ideas[ideaCursor]);
      setIdeaCursor(c => c + 1);
      return;
    }
    setIsLoadingIdeas(true);
    try {
      const response = await axios.post(`${API}/content/ideas/refresh`, {
        project_id: activeProjectId || null,
      }, { headers: getAuthHeader(), timeout: 90000 });
      const fresh = response.data.ideas || [];
      setIdeas(fresh);
      setIdeaCursor(0);
      if (fresh.length > 0) {
        applyIdeaToForm(fresh[0]);
        setIdeaCursor(1);
      } else {
        toast.error(t.kreation.actionFailed);
      }
    } catch (err) {
      // K8s 60s-Ingress kann trennen, während Backend weiterläuft — einmal recheck.
      if (err.code === 'ECONNABORTED' || !err.response) {
        try {
          await new Promise(r => setTimeout(r, 3000));
          const recheck = await axios.get(`${API}/content/ideas`, { headers: getAuthHeader() });
          const fresh = recheck.data?.ideas || [];
          if (fresh.length > 0) {
            setIdeas(fresh);
            applyIdeaToForm(fresh[0]);
            setIdeaCursor(1);
            return;
          }
        } catch { /* fall through */ }
      }
      toast.error(t.kreation.actionFailed);
    } finally {
      setIsLoadingIdeas(false);
    }
  };

  const fetchUserChannels = async (signal) => {
    try {
      const response = await axios.get(`${API}/user/profile`, {
        headers: getAuthHeader(),
        signal,
      });
      const channels = response.data.channel_settings || [];
      const activeChannels = channels.filter(c => c.active).map(c => c.platform);
      setUserChannels(activeChannels);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch channels:', err);
    }
  };

  const fetchVideoLimit = async (signal) => {
    try {
      const response = await axios.get(`${API}/video-limit`, {
        headers: getAuthHeader(),
        signal,
      });
      setVideoLimit(response.data);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch video limit:', err);
    }
  };

  const MAX_PLATFORMS = 5; // Maximum platforms at once for reliable generation
  
  const togglePlatform = (key) => {
    setSelectedPlatforms(prev => {
      if (prev.includes(key)) {
        return prev.filter(p => p !== key);
      }
      // Check YouTube video limit
      if (key === 'youtube') {
        if (videoLimit.remaining <= 0) {
          toast.error((t.kreation.videoLimit || '').replace('{limit}', String(videoLimit.limit)));
          return prev;
        }
      }
      // Check max limit
      if (prev.length >= MAX_PLATFORMS) {
        toast.warning(`Maximal ${MAX_PLATFORMS} Plattformen gleichzeitig auswählbar für zuverlässige Generierung`);
        return prev;
      }
      return [...prev, key];
    });
  };

  const handleFileUpload = async (uploadFiles) => {
    for (const file of uploadFiles) {
      const fileId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setFiles(prev => [...prev, {
        id: fileId,
        name: file.name,
        type: file.type.startsWith('image') ? 'image' : file.type.startsWith('video') ? 'video' : 'document',
        size: file.size,
        file: file
      }]);
    }
  };

  // Eigenes Referenzbild pro Post: als base64 lesen und direkt mitschicken
  // (umgeht bewusst den temp_-ID-Weg, damit die Referenz garantiert an der API ankommt)
  const handleCustomReferenceUpload = (file) => {
    if (!file || !file.type?.startsWith('image')) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 = String(dataUrl).split(',')[1] || '';
      setSelectedReferenceImageIds([]); // Galerie-Auswahl aufheben
      setCustomReferenceImage({ preview: dataUrl, base64 });
    };
    reader.readAsDataURL(file);
  };

  const removeFile = (fileId) => {
    setFiles(prev => {
      const next = prev.filter(f => f.id !== fileId);
      // Reset toggles if no images remain
      if (!next.some(f => f.type === 'image')) {
        setKeepInputText(false);
        setKeepUploadedPhotos(false);
      }
      return next;
    });
  };

  // Poll for batch status updates
  const startPollingBatchStatus = (batchId) => {
    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    setActiveBatchId(batchId);
    // Reset the one-shot guard for this new batch
    batchCompleteHandledRef.current = false;

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/content/batch/${batchId}/status`, {
          headers: getAuthHeader()
        });
        
        const { pieces, is_complete, completed, failed } = response.data;
        
        // Update generated content with latest status
        setGeneratedContent(pieces);
        
        // Check if all done
        if (is_complete && !batchCompleteHandledRef.current) {
          batchCompleteHandledRef.current = true;
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setActiveBatchId(null);
          
          if (completed > 0) {
            toast.success(t.common.toasts.contentPiecesGenerated.replace('{n}', completed).replace('{s}', completed === 1 ? '' : 's'));
          }
          if (failed > 0) {
            // Soft-recheck: some "failed" pieces actually have text_content
            // (post-success exception path). Wait 5s, refetch the batch and
            // count again. Only surface a real failure if it's still failed
            // with no content after the recheck window.
            setTimeout(async () => {
              try {
                const recheck = await axios.get(`${API}/content/batch/${batchId}/status`, { headers: getAuthHeader() });
                const recheckedPieces = recheck.data?.pieces || [];
                const stillBroken = recheckedPieces.filter(p =>
                  p.status === 'failed' && !(p.text_content || '').trim()
                );
                if (stillBroken.length > 0) {
                  toast.error(
                    t.common.toasts.generationTakingLonger.replace('{n}', stillBroken.length).replace('{s}', stillBroken.length === 1 ? '' : 's')
                  );
                } else if (recheckedPieces.length > 0) {
                  setGeneratedContent(recheckedPieces);
                  toast.success(t.common.toasts.allPiecesReady);
                }
              } catch {
                /* silent — original toast already covered the case */
              }
              setIsGenerating(false);
              fetchContentCount();
              if (refetchUsage) refetchUsage();
            }, 5000);
            return; // skip the immediate completion side-effects below
          }
          
          setIsGenerating(false);
          fetchContentCount();
          if (refetchUsage) refetchUsage();
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 2000); // Poll every 2 seconds
  };

  const handleGenerate = async () => {
    // Check limit first
    if (limitReached) {
      toast.error(t.kreation.limitReached);
      return;
    }
    
    if (!description.trim()) {
      toast.error(t.kreation.describeRequired);
      return;
    }
    if (selectedPlatforms.length === 0) {
      toast.error(t.kreation.platformRequired);
      return;
    }

    setIsGenerating(true);
    setGeneratedContent([]);

    try {
      const response = await axios.post(`${API}/content/generate`, {
        description: description,
        platforms: selectedPlatforms,
        file_ids: files.map(f => f.id),
        language: selectedLanguage,
        output_languages: outputLanguages,
        image_element: selectedElement || null,
        keep_input_text: keepInputText,
        keep_uploaded_photos: keepUploadedPhotos,
        project_id: activeProjectId || null,
        ...(selectedReferenceImageIds.length > 0 ? { reference_image_ids: selectedReferenceImageIds } : {}),
        ...(customReferenceImage?.base64 ? { custom_reference_image_base64: customReferenceImage.base64 } : {}),
      }, {
        headers: getAuthHeader(),
        timeout: 120000 //120 Seconds
      });

      const { content_pieces, batch_id, async: isAsync } = response.data;
      
      if (isAsync && batch_id) {
        // Async mode - show placeholders and start polling
        setGeneratedContent(content_pieces);
        // Single brief, calm info toast — no persistent loader.
        toast.info('To ensure the best quality, the generation can take up to 3 minutes. You will be updated once it\'s done.', {
          duration: 10000,
          dismissible: true,
          style: { background: '#0E2D2C', color: '#E2F8F6', borderLeft: '4px solid #00D9C8' },
        });
        startPollingBatchStatus(batch_id);
      } else {
        // Sync mode (fallback)
        setGeneratedContent(content_pieces || []);
        const count = content_pieces?.length || 0;
        toast.success(t.common.toasts.contentPiecesCreated.replace('{n}', count).replace('{s}', count === 1 ? '' : 's'));
        setIsGenerating(false);
        fetchContentCount();
        if (refetchUsage) refetchUsage();
      }
    } catch (err) {
      if (err.response?.status === 429 && err.response?.data?.error === 'LIMIT_REACHED') {
        toast.error(t.kreation.limitReachedShort);
        if (refetchUsage) refetchUsage();
        setIsGenerating(false);
        return;
      }

      // K8s trennt nach 60s — Backend läuft im Hintergrund weiter.
      // Network-Drop (kein err.response) und 502/503/504 silent behandeln.
      if (!err.response || [502, 503, 504].includes(err.response?.status)) {
        console.warn('Content generation: connection dropped (K8s timeout), backend still running:', err.code);
        setIsGenerating(false);
        return;
      }

      console.error('Content generation error:', err);
      toast.error(t.common.toasts.generationSlow);
      setIsGenerating(false);
    }
  };


  const handleRefreshIdeas = async () => {
    setIsLoadingIdeas(true);
    toast.info('To ensure the best quality, the generation can take up to 1 minute. You will be updated once it\'s done.', {
      duration: 8000,
      dismissible: true,
      style: { background: '#0E2D2C', color: '#E2F8F6', borderLeft: '4px solid #00D9C8' },
    });
    const requestStart = Date.now();
    try {
      const response = await axios.post(`${API}/content/ideas/refresh`, {
        project_id: activeProjectId || null,
      }, {
        headers: getAuthHeader(),
        timeout: 90000,
      });
      setIdeas(response.data.ideas || []);
      toast.success(t.kreation.ideasLoaded);
    } catch (err) {
      // K8s ingress (60s) may drop the connection while the backend keeps
      // generating. Recheck the ideas list once before showing an error.
      if (err.code === 'ECONNABORTED' || !err.response) {
        try {
          await new Promise(r => setTimeout(r, 3000));
          const recheck = await axios.get(`${API}/content/ideas`, { headers: getAuthHeader() });
          const freshIdeas = recheck.data?.ideas || [];
          const newest = freshIdeas.find(i => i.created_at && new Date(i.created_at).getTime() > requestStart - 5000);
          if (newest) {
            setIdeas(freshIdeas);
            toast.success(t.kreation.ideasLoaded);
            return;
          }
        } catch { /* fall through */ }
        // No fresh ideas yet and no real error response — stay quiet.
        return;
      }
      toast.error('Ideen konnten nicht geladen werden – bitte erneut versuchen. Dein Kontingent wird nicht verbraucht.');
    } finally {
      setIsLoadingIdeas(false);
    }
  };

  const [processingIdeaId, setProcessingIdeaId] = useState(null);
  const [savingIdeaId, setSavingIdeaId] = useState(null);

  const hasUploadedImages = files.some(f => f.type === 'image');

  const handleIdeaAction = async (ideaId, action, editedText = null) => {
    const idea = ideas.find(i => i.id === ideaId);
    
    if (action === 'reject') {
      // Reject is instant
      try {
        await axios.post(`${API}/content/ideas/${ideaId}/action`, {
          action,
          edited_text: null,
          language: idea?.language || selectedLanguage
        }, {
          headers: getAuthHeader()
        });
        setIdeas(prev => prev.filter(i => i.id !== ideaId));
        toast.success(t.kreation.ideaRejected);
      } catch (err) {
        toast.error(t.kreation.actionFailed);
      }
      return;
    }

    // Accept or Edit - these generate content
    if (action === 'edit') {
      setSavingIdeaId(ideaId);
    } else {
      setProcessingIdeaId(ideaId);
    }

    toast.info('To ensure the best quality, the generation can take up to 3 minutes. You will be updated once it\'s done.', {
      duration: 10000,
      dismissible: true,
      style: { background: '#0E2D2C', color: '#E2F8F6', borderLeft: '4px solid #00D9C8' },
    });
    const requestStart = Date.now();

    try {
      const response = await axios.post(`${API}/content/ideas/${ideaId}/action`, {
        action,
        edited_text: editedText,
        language: idea?.language || selectedLanguage,
        platform: idea?.platform || null,
        image_element: selectedElement || null,
        keep_input_text: keepInputText,
        keep_uploaded_photos: keepUploadedPhotos,
        project_id: activeProjectId || null,
      }, {
        headers: getAuthHeader(),
        timeout: 180000 // 3 Minuten für Content + Bild Generierung
      });

      setIdeas(prev => prev.filter(i => i.id !== ideaId));
      setEditingIdeaId(null);
      
      // Show preview of generated content (same as manual generation)
      if (response.data.content_piece) {
        setGeneratedContent([response.data.content_piece]);
      }
      
      toast.success(t.kreation.generateFromIdea);
      fetchContentCount();
    } catch (err) {
      // Resilience pattern (mirrors Produktion.js):
      //   • Network drop / 502 / 503 / 504  → silent. Backend often keeps
      //     running (K8s ingress 60s cutoff) and content appears on reload.
      //   • 5xx (≥500)                       → wait 15s, recheck DB; only
      //     surface toast if no new content landed.
      //   • 4xx                              → immediate error toast.
      const status = err.response?.status;
      const isNetworkOrGateway = !err.response || [502, 503, 504].includes(status);

      const findFreshContent = async () => {
        try {
          const checkResponse = await axios.get(`${API}/content`, {
            headers: getAuthHeader(),
            params: { limit: 5 },
          });
          const recentContent = checkResponse.data.content_pieces || [];
          return recentContent.filter(c => {
            const createdAt = c.created_at ? new Date(c.created_at).getTime() : 0;
            const fromIdea = c.from_idea_id === ideaId || c.source_idea_id === ideaId;
            return fromIdea || (createdAt >= requestStart - 5000);
          });
        } catch (_) {
          return [];
        }
      };

      const applyFreshContent = (newContent) => {
        setIdeas(prev => prev.filter(i => i.id !== ideaId));
        setEditingIdeaId(null);
        setGeneratedContent(newContent);
        toast.success(t.kreation.generateFromIdea);
        fetchContentCount();
      };

      if (isNetworkOrGateway) {
        // Quick recheck after 2s in case backend already finished.
        // If nothing yet → stay silent; reload / poller will resolve.
        try {
          await new Promise(r => setTimeout(r, 2000));
          const newContent = await findFreshContent();
          if (newContent.length > 0) {
            applyFreshContent(newContent);
          }
        } catch (_) { /* silent */ }
        return;
      }

      if (status >= 500 && status < 600) {
        // 5xx: give backend a fair 15s window, then either silent-success or error.
        setTimeout(async () => {
          const newContent = await findFreshContent();
          if (newContent.length > 0) {
            applyFreshContent(newContent);
            return;
          }
          console.error('Idea generation error:', err);
          toast.error('Generierung konnte nicht abgeschlossen werden – bitte erneut versuchen. Dein Kontingent wird nicht verbraucht.');
        }, 15000);
        return;
      }

      // 4xx — real client error, surface immediately.
      console.error('Idea generation error:', err);
      const msg = err.response?.data?.detail ||
        'Generierung konnte nicht abgeschlossen werden – bitte erneut versuchen. Dein Kontingent wird nicht verbraucht.';
      toast.error(msg);
    } finally {
      setProcessingIdeaId(null);
      setSavingIdeaId(null);
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'image': return <ImageIcon className="w-4 h-4" />;
      case 'video': return <Video className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getPlatformIcon = (platformKey) => {
    const platform = platforms.find(p => p.key === platformKey);
    if (platform) {
      const Icon = platform.icon;
      return <Icon className="w-4 h-4" style={{ color: platform.color }} />;
    }
    // Check custom channels
    const cc = customChannels.find(c => c.id === platformKey);
    if (cc) {
      return <Layers className="w-4 h-4" style={{ color: '#A0AEC0' }} />;
    }
    return null;
  };

  return (
    <div className="flex min-w-0 max-w-full" data-testid="kreation-page" onClick={() => { showLangDropdown && setShowLangDropdown(false); }}>
      {/* Input & Generation */}
      <div className="flex-1 min-w-0 max-w-full">
        {/* Header */}
        <div className="mb-4 lg:mb-6">
          <h1 className="text-2xl lg:text-4xl font-black tracking-tight text-white mb-1 lg:mb-2">
            {t.kreation.title}
          </h1>
          <p className="text-sm lg:text-base text-[#A0AEC0]">
            {t.kreation.subtitle}
          </p>
        </div>

        {/* 30 Days Ahead Banner */}
        <div
          className="relative overflow-hidden rounded-xl mb-4 max-w-4xl border border-[#00D9C8]/40 bg-gradient-to-r from-[#0B3A42] via-[#12303F] to-[#1A2B42] p-5 lg:p-6"
          data-testid="content-plan-banner"
        >
          <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-[#00D9C8]/10 blur-2xl pointer-events-none" />
          <div className="relative flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#00D9C8] text-[#0A1628] text-[11px] font-black tracking-wider">
                  <Sparkles className="w-3 h-3" />NEW
                </span>
              </div>
              <h2 className="text-xl lg:text-2xl font-black text-white leading-tight">
                {lang === 'de' ? 'Content für die nächsten 7, 14 oder 30 Tage, in einem Rutsch' : 'Content for the next 7, 14 or 30 days, in one go'}
              </h2>
            </div>
            <div className="flex items-center gap-2 lg:gap-3 shrink-0">
              {[7, 14, 30].map((d) => (
                <Link
                  key={d}
                  to={`/app/kreation/plan?days=${d}`}
                  className="group w-[72px] lg:w-20 py-2.5 rounded-lg bg-[#0A1628]/70 border border-[#00D9C8]/30 hover:border-[#00D9C8] hover:bg-[#00D9C8]/10 text-center transition-all"
                  data-testid={`content-plan-days-${d}`}
                >
                  <div className="text-2xl font-black text-white group-hover:text-[#00D9C8] leading-none">{d}</div>
                  <div className="text-[11px] text-[#8B9BB0] mt-1">{lang === 'de' ? 'Tage' : 'days'}</div>
                </Link>
              ))}
            </div>
          </div>
          <div className="relative mt-4">
            <Button asChild className="bg-[#00D9C8] hover:bg-[#00C4B4] text-[#0A1628] font-bold w-full lg:w-auto" data-testid="content-plan-btn">
              <Link to="/app/kreation/plan">
                <CalendarDays className="w-4 h-4 mr-2" />
                {lang === 'de' ? 'Jetzt erstellen' : 'Create now'}
              </Link>
            </Button>
          </div>
        </div>

        {/* Input Section */}
        <Card className="bg-[#1A2B42] border-transparent mb-4 max-w-4xl">
          <CardContent className="p-4 lg:p-5">
            {/* Generate Idea Button */}
            <div className="flex items-center justify-between gap-3 mb-3">
              <h2 className="text-base lg:text-lg font-semibold text-[#E3EAF3] leading-tight" data-testid="single-content-heading">
                {lang === 'de' ? 'Mit einer Idee in alle Kanäle' : 'One idea, every channel'}
              </h2>
              <Button
                onClick={handleGenerateIdea}
                disabled={isLoadingIdeas}
                variant="ghost"
                size="sm"
                className="text-[#00D9C8] hover:bg-[#00D9C8]/10 border border-[#00D9C8]/30"
                data-testid="generate-idea-btn"
              >
                {isLoadingIdeas ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />{t.kreation.generateIdea}</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-1.5" />{t.kreation.generateIdea}</>
                )}
              </Button>
            </div>

            {/* Description Textarea with Attachment Button */}
            <div className="relative mb-4">
              <Textarea
                placeholder={rotatingPlaceholder}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="min-h-[96px] bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white placeholder:text-[#8B9BB0] resize-none pr-11"
                data-testid="content-description-input"
              />
              
              {/* Attachment - seamless icon, bottom left */}
              <label
                className="absolute bottom-3 right-3 flex items-center justify-center w-7 h-7 text-[#8B9BB0] hover:text-[#00D9C8] cursor-pointer transition-colors"
                data-testid="file-attach-button"
                title={t.kreation.uploadFiles}
              >
                <Paperclip className="w-[18px] h-[18px]" />
                <input
                  type="file"
                  multiple
                  accept=".jpg,.jpeg,.png,.webp,.pdf,.docx,.txt"
                  className="hidden"
                  onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                />
              </label>
            </div>
            
            {/* Uploaded Files */}
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {files.map(file => (
                  <div 
                    key={file.id}
                    className="flex items-center gap-1.5 px-2 py-1 bg-[#0A1628] rounded text-xs"
                  >
                    {getFileIcon(file.type)}
                    <span className="text-white max-w-[100px] truncate">{file.name}</span>
                    <button 
                      onClick={() => removeFile(file.id)}
                      className="text-[#5A6C7D] hover:text-white"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Erweiterte Einstellungen */}
            <div className="mb-4" data-testid="advanced-settings">
              <div className="flex flex-wrap items-center gap-3">
                {/* Language Selector */}
                <div className="relative" data-testid="language-selector" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => { setShowLangDropdown(!showLangDropdown); setShowElementDropdown(false); }}
                    className="flex items-center gap-2 px-3 py-1.5 bg-[#0A1628] border border-[#2D3F59] hover:border-[#00D9C8]/50 rounded text-sm transition-all"
                    data-testid="language-selector-btn"
                  >
                    <Globe className="w-3.5 h-3.5 text-[#00D9C8]" />
                    <span className="text-white font-medium">{LANGUAGE_OPTIONS.find(l => l.code === selectedLanguage)?.abbr || 'DE'}</span>
                  </button>
                  {showLangDropdown && (
                    <div className="absolute left-0 top-full mt-1 bg-[#1A2B42] border border-[#2D3F59] rounded shadow-lg z-50 min-w-[130px]">
                      {multiLangAllowed && (
                        <div className="px-3 py-2 text-[11px] text-[#6B7A94] border-b border-[#2D3F59]">
                          {t.kreation.multiLang.hint}
                        </div>
                      )}
                      {LANGUAGE_OPTIONS.map(lang => {
                        const idx = outputLanguages.indexOf(lang.code);
                        const isSelected = idx !== -1;
                        const isPrimary = idx === 0;
                        return (
                          <button
                            key={lang.code}
                            onClick={() => toggleLanguage(lang.code)}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-[#0A1628] transition-colors ${isSelected ? 'text-[#00D9C8]' : 'text-[#A0AEC0]'}`}
                            data-testid={`lang-option-${lang.code}`}
                          >
                            <span className="flex items-center gap-2">
                              {isSelected && <Check className="w-3.5 h-3.5" />}
                              <span>{lang.label}</span>
                              {isPrimary && outputLanguages.length > 1 && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00D9C8]/15 text-[#00D9C8] font-medium">{t.kreation.multiLang.primary}</span>
                              )}
                            </span>
                            <span className="font-mono font-bold">{lang.abbr}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Eingabetext beibehalten Switch */}
                <label className="flex items-center gap-2 cursor-pointer" data-testid="keep-input-text-toggle">
                  <Switch
                    checked={keepInputText}
                    onCheckedChange={setKeepInputText}
                    className="data-[state=checked]:bg-[#00D9C8] data-[state=unchecked]:bg-[#3D4F65] border border-[#0A1628] shadow-sm"
                  />
                  <span className={`text-sm ${keepInputText ? 'text-[#00D9C8]' : 'text-[#A0AEC0]'}`}>{t.kreation.keepInputText}</span>
                </label>

                {/* Fotos beibehalten Switch - nur bei Bild-Upload */}
                {hasUploadedImages && (
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="keep-uploaded-photos-toggle">
                    <Switch
                      checked={keepUploadedPhotos}
                      onCheckedChange={setKeepUploadedPhotos}
                      className="data-[state=checked]:bg-[#00D9C8] data-[state=unchecked]:bg-[#3D4F65] border border-[#0A1628] shadow-sm"
                    />
                    <span className={`text-sm ${keepUploadedPhotos ? 'text-[#00D9C8]' : 'text-[#A0AEC0]'}`}>{t.kreation.keepPhotos}</span>
                  </label>
                )}
              </div>
            </div>

            {/* Platform Selector */}
            <div className="mb-4">
              <p className="text-sm font-semibold text-white mb-2">{t.kreation.selectPlatforms}</p>
              <div className="grid grid-cols-3 lg:grid-cols-4 gap-1.5 lg:gap-2">
                {activePlatforms.map(platform => {
                  const Icon = platform.icon;
                  const isSelected = selectedPlatforms.includes(platform.key);
                  return (
                    <button
                      key={platform.key}
                      onClick={() => togglePlatform(platform.key)}
                      className={`flex flex-col lg:flex-row items-center gap-1 lg:gap-2 px-2 lg:px-3 py-2 transition-all ${
                        isSelected 
                          ? 'bg-[#00D9C8]/10 border border-[#00D9C8]' 
                          : 'bg-[#0A1628] border border-transparent hover:border-[#00D9C8]/50'
                      }`}
                      data-testid={`platform-${platform.key}`}
                    >
                      <Icon className="w-4 h-4" style={{ color: isSelected ? platform.color : '#5A6C7D' }} />
                      <span className={`text-[10px] lg:text-xs text-center lg:text-left ${isSelected ? 'text-white' : 'text-[#A0AEC0]'}`}>
                        {t.platforms[platform.key] || platform.label}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-[#A0AEC0] mt-2">
                {selectedPlatforms.length}/{MAX_PLATFORMS} {t.kreation.platformsSelected.replace('{{max}}', MAX_PLATFORMS)}
              </p>
            </div>

            {/* Reference-Image-Selector für Bildgenerierung (reaktiviert Juli 2026).
                User wählt pro Post entweder ein vorhandenes Brand-Referenzbild
                ODER lädt ein eigenes hoch. Das bestimmt den Stil für DIESEN Post. */}
            {(brandRefImages.length > 0 || true) && (
              <div className="mb-3" data-testid="reference-image-selector">
                <p className="text-xs text-[#A0AEC0] mb-2">{t.kreation.refImages.title}</p>
                <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                  {/* Upload-Kachel: eigenes Referenzbild pro Post */}
                  <label
                    className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden cursor-pointer flex flex-col items-center justify-center gap-1 border border-dashed border-[#1e9ead]/50 hover:border-[#1e9ead] text-[#1e9ead] transition-all"
                    data-testid="reference-image-upload"
                  >
                    {customReferenceImage ? (
                      <img src={customReferenceImage.preview} alt={t.kreation.refImages.ownAlt} className="w-full h-full object-cover" />
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        <span className="text-[10px] leading-tight text-center px-1">{t.kreation.refImages.ownTile}</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleCustomReferenceUpload(e.target.files?.[0])}
                    />
                  </label>

                  {/* Galerie: vorhandene Brand-Referenzbilder — Multi-Select bis 4 */}
                  {brandRefImages.map(img => {
                    const active = selectedReferenceImageIds.includes(img.id) && !customReferenceImage;
                    return (
                      <button
                        key={img.id}
                        type="button"
                        onClick={() => {
                          setCustomReferenceImage(null);
                          setSelectedReferenceImageIds(prev => {
                            if (prev.includes(img.id)) {
                              return prev.filter(id => id !== img.id);
                            }
                            if (prev.length >= 4) return prev; // hard cap: gpt-image-2 supports max 4 refs
                            return [...prev, img.id];
                          });
                        }}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden transition-all relative ${active ? 'ring-2 ring-[#1e9ead] ring-offset-2 ring-offset-[#0A1628]' : 'border border-[#1A2B42] hover:border-[#1e9ead]/50'}`}
                        data-testid={`reference-image-thumb-${img.id}`}
                        aria-pressed={active}
                      >
                        <img
                          src={img.file_url}
                          alt={img.filename || t.kreation.refImages.thumbAlt}
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                        {active && (
                          <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#1e9ead] text-white text-[11px] font-semibold flex items-center justify-center shadow">
                            {selectedReferenceImageIds.indexOf(img.id) + 1}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-[#A0AEC0] mt-2">
                  {customReferenceImage
                    ? t.kreation.refImages.hintCustom
                    : selectedReferenceImageIds.length > 0
                      ? (selectedReferenceImageIds.length > 1
                          ? t.kreation.refImages.hintSelectedMany
                          : t.kreation.refImages.hintSelectedOne
                        ).replace('{n}', selectedReferenceImageIds.length)
                      : t.kreation.refImages.hintNone}
                </p>
              </div>
            )}

            {/* Generate Button */}
            {limitReached ? (
              <div className="w-full p-3 lg:p-4 bg-red-500/10 border border-red-500/30 rounded-lg" data-testid="generate-blocked">
                <div className="flex items-center gap-2 text-red-400">
                  <AlertTriangle className="w-4 h-4 lg:w-5 lg:h-5" />
                  <span className="text-sm font-medium">{lang === 'de' ? 'Deine Credits sind aufgebraucht' : 'You are out of credits'}</span>
                </div>
                <p className="text-xs text-red-400/80 mt-1">
                  {lang === 'de' ? 'Generieren ist pausiert, bis dein Plan zurückgesetzt wird oder du Credits hinzufügst.' : 'Generating is paused until your plan resets or you add credits.'}
                </p>
                <Link to="/app/upgrade" className="inline-block mt-2 text-xs font-semibold px-3 py-1.5 rounded bg-[#00D9C8] text-[#0A1628] hover:bg-[#00C4B4]">
                  {lang === 'de' ? 'Credits hinzufügen' : 'Add credits'}
                </Link>
              </div>
            ) : (
              <Button 
                onClick={handleGenerate}
                disabled={isGenerating || !description.trim() || selectedPlatforms.length === 0}
                className="w-full h-10 lg:h-12 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-semibold disabled:opacity-50 text-sm lg:text-base"
                data-testid="generate-content-btn"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 lg:w-5 lg:h-5 mr-2 animate-spin" />
                    <span className="hidden sm:inline">{t.kreation.generating}</span>
                    <span className="sm:hidden">{t.common.generating}</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 lg:w-5 lg:h-5 mr-2" />
                    {t.kreation.generateBtn}
                  </>
                )}
              </Button>
            )}
            
            {/* Warning Banner at 80% */}
            {warning && !limitReached && (
              <div className="w-full p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-amber-400 text-xs">
                  <AlertTriangle className="w-3 h-3" />
                  <span>{lang === 'de' ? `Du hast ${Math.round(usage?.percentUsed || 80)} % deiner Credits verbraucht.` : `You have used ${Math.round(usage?.percentUsed || 80)}% of your credits.`}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Generated Content Preview - Shows during and after generation */}
        {generatedContent.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                {isGenerating ? (
                  <>
                    <Loader2 className="w-5 h-5 text-[#00D9C8] animate-spin" />
                    {t.kreation.generating}
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 text-[#00D9C8]" />
                    {generatedContent.filter(c => c.status !== 'generating' && c.status !== 'failed').length} Content Piece{generatedContent.filter(c => c.status !== 'generating' && c.status !== 'failed').length !== 1 ? 's' : ''} {t.common.success.toLowerCase()}
                  </>
                )}
              </h3>
              {!isGenerating && (
                <Link 
                  to="/app/produktion"
                  className="text-[#00D9C8] text-sm hover:underline inline-flex items-center gap-1"
                >
                  {t.produktion.allContent}
                  <ExternalLink className="w-3 h-3" />
                </Link>
              )}
            </div>
            {generatedContent.map((content, index) => {
              const isGeneratingItem = content.status === 'generating';
              const isFailed = content.status === 'failed';
              const platformInfo = platforms.find(p => p.key === content.platform);
              const PlatformIcon = platformInfo?.icon;
              
              return (
                <motion.div
                  key={content.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className={`overflow-hidden transition-all ${
                    isGeneratingItem 
                      ? 'bg-[#0A1628] border-[#2D3F59] opacity-70' 
                      : isFailed
                        ? 'bg-[#1A2B42] border-red-500/50'
                        : 'bg-[#1A2B42] border-transparent hover:border-[#00D9C8]'
                  }`}>
                    <div className="flex">
                      {/* Image Preview or Placeholder */}
                      <div className="w-24 h-24 flex-shrink-0 bg-[#0A1628] flex items-center justify-center">
                        {content.image_urls?.length > 0 ? (
                          <img 
                            src={content.image_urls[0]} 
                            alt={content.title}
                            className="w-full h-full object-cover"
                          />
                        ) : isGeneratingItem ? (
                          <Loader2 className="w-8 h-8 text-[#5A6C7D] animate-spin" />
                        ) : PlatformIcon ? (
                          <PlatformIcon className="w-10 h-10" style={{ color: platformInfo?.color || '#5A6C7D' }} />
                        ) : (
                          <FileText className="w-10 h-10 text-[#5A6C7D]" />
                        )}
                      </div>
                      <CardContent className="p-3 flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            {PlatformIcon && (
                              <PlatformIcon 
                                className="w-4 h-4" 
                                style={{ color: isGeneratingItem ? '#5A6C7D' : platformInfo?.color }} 
                              />
                            )}
                            <Badge className={`text-xs ${
                              isGeneratingItem 
                                ? 'bg-[#2D3F59] text-[#5A6C7D] border-[#5A6C7D]'
                                : 'bg-[#0A1628] text-[#00D9C8] border-[#00D9C8]'
                            }`}>
                              {t.platforms?.[content.platform] || platformInfo?.label || content.platform}
                            </Badge>
                            {content.content_number && (
                              <span className="text-xs text-[#5A6C7D]">#{content.content_number}</span>
                            )}
                          </div>
                          <Badge className={`text-xs ${
                            isGeneratingItem 
                              ? 'bg-yellow-500/20 text-yellow-500'
                              : isFailed
                                ? 'bg-red-500/20 text-red-500'
                                : 'bg-green-500/20 text-green-500'
                          }`}>
                            {isGeneratingItem ? t.common.generating : isFailed ? t.common.failed : t.common.draft}
                          </Badge>
                        </div>
                        
                        {isGeneratingItem ? (
                          <div className="space-y-2">
                            <div className="h-3 bg-[#2D3F59] rounded animate-pulse w-3/4"></div>
                            <div className="h-2 bg-[#2D3F59] rounded animate-pulse w-full"></div>
                            <div className="h-2 bg-[#2D3F59] rounded animate-pulse w-2/3"></div>
                          </div>
                        ) : (
                          <>
                            <h4 className="text-sm font-medium text-white mb-1 line-clamp-1">{content.title}</h4>
                            <p className="text-xs text-[#A0AEC0] line-clamp-2 mb-2">
                              {content.text_content}
                            </p>
                            {!isFailed && (
                              <Link 
                                to={`/app/produktion?content=${content.id}`}
                                className="text-[#00D9C8] text-xs hover:underline inline-flex items-center gap-1"
                                data-testid={`view-content-${content.id}`}
                              >
                                {t.kreation.toProduction}
                                <ExternalLink className="w-3 h-3" />
                              </Link>
                            )}
                          </>
                        )}
                      </CardContent>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
};

export default Kreation;